import { AspectRatio, IconButton } from '@chakra-ui/react'
import { ViewableTracker, ViewableTrackerProps } from '@src/components/sco/ViewableTracker'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { SSRGate } from '@src/components/sco/utilities/SSRGate'
import { useCallback, useEffect, useRef, useState } from 'react'
import { BsPlayFill } from 'react-icons/bs'
import ReactPlayer from 'react-player'

type TrackedVideoPlayerProps = ViewableTrackerProps & {
  autoPlay?: boolean
  borderRadius?: string
  imageUrl?: string
  playbackEndInSeconds?: number
  playbackStartInSeconds?: number
  videoDurationInSeconds?: number
  videoPlaybackInSeconds?: number
  videoUrl?: string
}

export const TrackedVideoPlayer = (props: TrackedVideoPlayerProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const playerRef = useRef<ReactPlayer>(null)
  const [playTrigger, setPlayTrigger] = useState(props.autoPlay)
  const [playbackInSeconds, setPlaybackInSeconds] = useState<number>(props.videoPlaybackInSeconds)
  const [totalDurationInSeconds, setTotalDurationInSeconds] = useState<number>(props.videoDurationInSeconds)
  const [playbackStartInSeconds, setPlaybackStartInSeconds] = useState<number>(0)
  const [playbackEndInSeconds, setPlaybackEndInSeconds] = useState<number>(0)

  const onReady = useCallback(() => {
    if (playbackStartInSeconds && playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(playbackStartInSeconds, 'seconds')
    }
  }, [playbackStartInSeconds, playerRef.current])

  const onDuration = useCallback(
    (duration: number) => {
      setTotalDurationInSeconds(duration)
      setPlaybackEndInSeconds(props.playbackEndInSeconds || duration)
    },
    [props.playbackEndInSeconds]
  )

  const onProgress = useCallback(progress => {
    setPlaybackInSeconds(progress.playedSeconds)
  }, [])

  useEffect(() => {
    if (playbackStartInSeconds && playerRef.current && playerRef.current.seekTo) {
      playerRef.current.seekTo(playbackStartInSeconds, 'seconds')
    }
  }, [playbackStartInSeconds])

  useEffect(() => {
    if (props.playbackStartInSeconds) {
      setPlaybackStartInSeconds(props.playbackStartInSeconds)
    }
    if (props.playbackEndInSeconds) {
      setPlaybackEndInSeconds(props.playbackEndInSeconds)
    }
  }, [playerRef.current, props.playbackStartInSeconds, props.playbackEndInSeconds])

  return (
    <ViewableTracker
      playbackPositionInSeconds={playbackInSeconds}
      startedAt={playbackInSeconds > 0 ? new Date() : null}
      trackScrolling={false}
      viewableId={props.viewableId}
      viewableType={props.viewableType}
      completionAsPercent={
        Math.round(100 * (playbackInSeconds - playbackStartInSeconds)) /
        (playbackEndInSeconds - playbackStartInSeconds ||
          totalDurationInSeconds ||
          props.videoDurationInSeconds ||
          1)
      }
      {...props}
    >
      <AspectRatio
        bgColor={'black'}
        borderRadius={props.borderRadius || getBreakpointValue(['lg', 'xl'])}
        h={'full'}
        ratio={16 / 9}
        width={'100%'}
      >
        <SSRGate disableSSR={true}>
          <ReactPlayer
            key={props.videoUrl}
            ref={playerRef}
            controls
            height={'auto'}
            light={playbackStartInSeconds > 0 || props.autoPlay ? false : props.imageUrl}
            pip={true}
            playing={playTrigger || props.autoPlay ? true : false}
            progressInterval={2000}
            url={props.videoUrl}
            width={'100%'}
            config={{
              file: {
                attributes: {
                  autoload: true,
                  autoplay: props.autoPlay,
                },
              },
              vimeo: {
                playerOptions: {
                  autoload: true,
                  autoplay: props.autoPlay,
                },
              },
            }}
            playIcon={
              <IconButton
                aria-label={'play'}
                backdropFilter={'blur(20px)'}
                bgColor={'blackAlpha.600'}
                boxSize={20}
                fontSize={'4xl'}
                onClick={() => setPlayTrigger(true)}
              >
                <BsPlayFill />
              </IconButton>
            }
            style={{
              overflow: 'hidden',
              borderRadius: props.borderRadius || `${getBreakpointValue(['0.5rem', '0.75rem'])}`,
            }}
            onDuration={onDuration}
            onProgress={onProgress}
            onReady={onReady}
          />
        </SSRGate>
      </AspectRatio>
    </ViewableTracker>
  )
}
